import apiClient from '../utils/apiClient';

/**
 * #162225 — Đối soát camera ↔ bill (module `ai-monitoring` phía server, có từ 25/03/2026).
 *
 * Backend đã có đủ 3 endpoint từ trước, admin chưa hề gọi tới — màn
 * `AiMonitoringAlertsPage` là nơi đầu tiên dùng chúng.
 */
export const aiMonitoringAPI = {
  /** Danh sách cảnh báo, lọc theo chi nhánh / loại / trạng thái xử lý / khoảng thời gian. */
  getAlerts: async (params = {}) =>
    apiClient.get({ url: '/ai-monitoring/alerts', params }),

  /** Đánh dấu đã xử lý + ghi chú (server tự ghi `resolved_by` theo người đăng nhập). */
  resolveAlert: async (id, note) =>
    apiClient.post({
      url: `/ai-monitoring/alerts/${id}/resolve`,
      data: { note: note || undefined },
    }),

  /**
   * #181800 — LỊCH SỬ NHẬN DIỆN (mỗi lần hệ thống bắt được vùng có khách và gửi tin). Lọc theo bàn,
   * vùng khoanh, ngày. Chỉ có vùng sếp đã khoanh.
   */
  getDetections: async (params = {}) =>
    apiClient.get({ url: '/ai-monitoring/detections', params }),

  getDetectionImage: async (id, branchId) =>
    apiClient.get({
      url: `/ai-monitoring/detections/${encodeURIComponent(id)}/image`,
      params: { branch_id: branchId },
      responseType: 'blob',
    }),

  /** Lệnh sếp 26/10/2026 — QC nhận diện (Haiku) trước khi gửi tin: danh sách + số liệu đúng/sai. */
  // Lệnh sếp 31/10/2026 — thống kê nhân viên xác nhận bàn + bill camera (`branch_id=all` chỉ ADMIN).
  getCambillConfirmStats: async (params = {}) =>
    apiClient.get({ url: '/ai-monitoring/cambill/confirm-stats', params }),

  getQcChecks: async (params = {}) =>
    apiClient.get({ url: '/ai-monitoring/qc-checks', params }),

  getQcImage: async (id, branchId) =>
    apiClient.get({
      url: `/ai-monitoring/qc-checks/${encodeURIComponent(id)}/image`,
      params: { branch_id: branchId },
      responseType: 'blob',
    }),

  /** Chấm QC: `correct` true = QC đúng, false = QC sai, null = bỏ chấm. */
  gradeQcCheck: async (id, branchId, correct) =>
    apiClient.post({
      url: `/ai-monitoring/qc-checks/${encodeURIComponent(id)}/grade`,
      params: { branch_id: branchId },
      data: { correct },
    }),

  /** Đối chiếu trực tiếp camera ↔ session tại thời điểm hiện tại, không cần chờ cảnh báo. */
  getComparison: async (branchId) =>
    apiClient.get({ url: '/ai-monitoring/comparison', params: { branchId } }),

  /**
   * #181800 — đặt NGƯỠNG ĐỘ TIN CẬY AI (0..1) của chi nhánh: vùng thấp hơn ngưỡng không được lưu
   * và không vào tin Telegram (áp từ lượt nhận vùng kế tiếp). Chỉ ADMIN/MANAGER.
   */
  getRegionSettings: async (branchId) =>
    apiClient.get({ url: '/ai-monitoring/regions/settings', params: { branch_id: branchId } }),

  /**
   * Lệnh sếp 18/10/2026 — 2 công tắc camera-bill: `notify_nv_enabled` (gửi tin riêng cho NV trong ca),
   * `penalty_enabled` (trừ điểm khi quá hạn không ai xác nhận). Gửi trường nào đổi trường đó. Chỉ ADMIN.
   */
  setCambillSwitches: async (branchId, switches) =>
    apiClient.put({
      url: '/ai-monitoring/regions/settings/cambill',
      data: switches,
      params: { branch_id: branchId },
    }),

  /**
   * Lệnh sếp 18/10/2026 — SO SÁNH MÔ HÌNH / TÀI KHOẢN nhận diện người (tab "So sánh mô hình", chỉ ADMIN).
   */
  benchAccounts: async () => apiClient.get({ url: '/ai-monitoring/vision-bench/accounts' }),
  benchCreateAccount: async (data) => apiClient.post({ url: '/ai-monitoring/vision-bench/accounts', data }),
  benchUpdateAccount: async (id, data) =>
    apiClient.patch({ url: `/ai-monitoring/vision-bench/accounts/${encodeURIComponent(id)}`, data }),
  benchDeleteAccount: async (id) =>
    apiClient.delete({ url: `/ai-monitoring/vision-bench/accounts/${encodeURIComponent(id)}` }),
  benchYoloModels: async (branchId) =>
    apiClient.get({ url: '/ai-monitoring/vision-bench/yolo-models', params: { branch_id: branchId } }),
  benchCompanyAccounts: async (branchId) =>
    apiClient.get({ url: '/ai-monitoring/vision-bench/company-accounts', params: { branch_id: branchId } }),
  benchSyncSamples: async (branchId) =>
    apiClient.post({ url: '/ai-monitoring/vision-bench/samples/sync', params: { branch_id: branchId } }),
  benchSamples: async (params) => apiClient.get({ url: '/ai-monitoring/vision-bench/samples', params }),
  benchSetTruth: async (id, truthCount) =>
    apiClient.put({
      url: `/ai-monitoring/vision-bench/samples/${encodeURIComponent(id)}/truth`,
      data: { truth_count: truthCount },
    }),
  benchSampleImage: async (id) =>
    apiClient.get({
      url: `/ai-monitoring/vision-bench/samples/${encodeURIComponent(id)}/image`,
      responseType: 'blob',
    }),
  benchStartRun: async (data) => apiClient.post({ url: '/ai-monitoring/vision-bench/runs', data }),
  benchRuns: async () => apiClient.get({ url: '/ai-monitoring/vision-bench/runs' }),
  benchRunResults: async (id, params) =>
    apiClient.get({ url: `/ai-monitoring/vision-bench/runs/${encodeURIComponent(id)}/results`, params }),
  benchCancelRun: async (id) =>
    apiClient.post({ url: `/ai-monitoring/vision-bench/runs/${encodeURIComponent(id)}/cancel` }),
  benchPauseRun: async (id) =>
    apiClient.post({ url: `/ai-monitoring/vision-bench/runs/${encodeURIComponent(id)}/pause` }),
  benchResumeRun: async (id) =>
    apiClient.post({ url: `/ai-monitoring/vision-bench/runs/${encodeURIComponent(id)}/resume` }),
  benchPairs: async (params) => apiClient.get({ url: '/ai-monitoring/vision-bench/pairs', params }),
  benchSetPairTruth: async (id, truthDup) =>
    apiClient.put({
      url: `/ai-monitoring/vision-bench/pairs/${encodeURIComponent(id)}/truth`,
      data: { truth_dup: truthDup },
    }),
  benchPairLeaderboard: async (branchId) =>
    apiClient.get({ url: '/ai-monitoring/vision-bench/pairs/leaderboard', params: { branch_id: branchId } }),
  benchLeaderboard: async (branchId) =>
    apiClient.get({ url: '/ai-monitoring/vision-bench/leaderboard', params: { branch_id: branchId } }),

  setRegionSettings: async (branchId, minRegionConfidence) =>
    apiClient.put({
      url: '/ai-monitoring/regions/settings',
      data: { min_region_confidence: minRegionConfidence },
      params: { branch_id: branchId },
    }),

  /**
   * #223029 — KHOANH VÙNG trên ảnh camera (app Admin). Danh sách camera + trạng thái đã khoanh,
   * ảnh mẫu (blob), kho luật tách vùng có phiên bản. Ghi chỉ ADMIN/MANAGER (máy chủ gác).
   */
  listRegionCameras: async (branchId) =>
    apiClient.get({
      url: '/ai-monitoring/region-splits/cameras',
      params: { branch_id: branchId },
    }),

  getRegionCameraImage: async (serial, branchId, ngay, gio) =>
    apiClient.get({
      url: `/ai-monitoring/region-splits/cameras/${encodeURIComponent(serial)}/image`,
      params: { branch_id: branchId, ngay: ngay || undefined, gio: gio || undefined },
      responseType: 'blob',
    }),

  getRegionSplit: async (serial, branchId) => {
    const r = await apiClient.get({
      url: '/ai-monitoring/region-splits',
      params: { branch_id: branchId, camera_serial: serial },
    });
    return r?.set ?? null;
  },

  getRegionSplitHistory: async (serial, branchId) => {
    const r = await apiClient.get({
      url: '/ai-monitoring/region-splits/history',
      params: { branch_id: branchId, camera_serial: serial },
    });
    return r?.versions ?? [];
  },

  saveRegionSplit: async (body) => {
    const r = await apiClient.post({ url: '/ai-monitoring/region-splits', data: body });
    return r.set;
  },

  restoreRegionSplit: async (id, branchId) => {
    const r = await apiClient.post({
      url: `/ai-monitoring/region-splits/${id}/restore`,
      data: {},
      params: { branch_id: branchId },
    });
    return r.set;
  },
};
